import type { EnhancementSettings } from "./types";
import { DEFAULT_SETTINGS } from "./types";

const SETTINGS_KEY = "sfe-settings";
const PROXY_URL_KEY = "sfe-proxy-url";

/**
 * Canonical public hostname.
 * Prefer www until apex DNS has fully propagated for Let's Encrypt.
 */
export const DEFAULT_PROXY_URL =
  import.meta.env?.VITE_PROXY_URL ?? "https://www.wheel-trader.us";

const PROXY_HOST = "www.wheel-trader.us";
const OWN_PROXY_HOSTS = new Set([
  "wheel-trader.us",
  "www.wheel-trader.us",
  "172.239.64.187",
  "172-239-64-187.sslip.io",
]);

export function loadSettings(): EnhancementSettings {
  try {
    const raw = localStorage.getItem(SETTINGS_KEY);
    if (!raw) return { ...DEFAULT_SETTINGS };
    return { ...DEFAULT_SETTINGS, ...JSON.parse(raw) };
  } catch {
    return { ...DEFAULT_SETTINGS };
  }
}

export function saveSettings(settings: EnhancementSettings): void {
  localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
}

function normalizeProxyUrl(url: string): string {
  const trimmed = url.trim().replace(/\/$/, "");
  try {
    const parsed = new URL(trimmed);
    if (OWN_PROXY_HOSTS.has(parsed.hostname)) {
      // Stay on whatever owned host the user is already using (apex/www/sslip),
      // except rewrite bare IP → canonical www.
      if (parsed.hostname === "172.239.64.187") {
        parsed.hostname = PROXY_HOST;
      }
      parsed.protocol = "https:";
      return parsed.toString().replace(/\/$/, "");
    }
  } catch {
    return trimmed;
  }
  return trimmed;
}

export function loadProxyUrl(): string {
  // Same-origin when the app is already served from our Linode hostnames.
  try {
    if (typeof window !== "undefined" && OWN_PROXY_HOSTS.has(window.location.hostname)) {
      return window.location.origin;
    }
  } catch {
    /* ignore */
  }
  const raw =
    localStorage.getItem(PROXY_URL_KEY) ??
    import.meta.env?.VITE_PROXY_URL ??
    DEFAULT_PROXY_URL;
  const normalized = normalizeProxyUrl(raw);
  if (normalized !== raw) saveProxyUrl(normalized);
  return normalized;
}

export function saveProxyUrl(url: string): void {
  localStorage.setItem(PROXY_URL_KEY, normalizeProxyUrl(url));
}

export function getApiConfig(): { baseUrl: string } {
  const proxyUrl = loadProxyUrl();
  return {
    baseUrl: proxyUrl || window.location.origin,
  };
}
